// Part of the agent UI for spark#246 / @adr SPARK-0034-ai-agents-are-project-context-not-team-members@2: an AI agent is PROJECT CONTEXT, never a team
// member. Nothing here reaches the solver, and no agent carries a Powers profile — Powers is a
// psychometric instrument built for humans, and putting agents on that scale would argue they are
// interchangeable with candidates, which is the model @adr SPARK-0034-ai-agents-are-project-context-not-team-members@2 rejects.

import { FormattedMessage, useIntl } from "react-intl";
import { AgentMonogram } from "./AgentMonogram";
import { agentHref, agentProse } from "./agents";
import type { Agent } from "./agents";
import type { ReactNode } from "react";

/** A micro-label section of the agent sheet; the admin editor draws its sections through it too. */
export function AgentColumn({ label, count, children }: { label: ReactNode; count?: number; children: ReactNode }) {
  return (
    <div>
      <div className="mb-3 flex items-baseline gap-2 text-smycles-micro tracking-[0.25em] text-ink-muted">
        <span className="h-px w-4 bg-accent" aria-hidden />
        {label}
        {count != null ? <span className="text-ink-muted tabular-nums">{count}</span> : null}
      </div>
      {children}
    </div>
  );
}

/**
 * The full agent sheet: role, what it covers, tools, integrations, guardrails. Never Powers.
 *
 * The content of `/a/:id`, which is the agent's only detail view: every agent row in Match links
 * there. Carries its own top padding because the page's box pads only the sides and bottom.
 *
 * A column with nothing in it is left out: an admin-created agent may carry only a role, and an
 * empty "Tools 0" heading says nothing the absence does not.
 */
export function AgentDetail({
  agent,
  action,
}: {
  agent: Agent;
  /** Right end of the header row. */
  action?: ReactNode;
}) {
  const integrations = agent.integrations;
  // Prose falls back to the other language where the reader's is missing (genesis `localized`);
  // such a text carries its own `lang` so a screen reader pronounces it right. An entry with no
  // text at all is left out, and counts follow what is shown.
  const { locale } = useIntl();
  const prose = (t: Parameters<typeof agentProse>[0]) => agentProse(t, locale);
  const role = prose(agent.role);
  const covers = agent.covers.map(prose).filter((c) => c.text);
  const guardrails = agent.guardrails.map(prose).filter((g) => g.text);
  const spec = [agent.vendor, agent.model].filter(Boolean).join(" / ");
  const href = agentHref(agent.url);
  const nameClass = "font-serif text-smycles-title leading-none";
  return (
    <div className="pt-5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-ink/10 pb-5">
        <AgentMonogram name={agent.name} />
        <div className="min-w-0">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              className={`${nameClass} underline decoration-ink/30 underline-offset-2 transition hover:text-ink-accent focus-visible:outline focus-visible:outline-1 focus-visible:outline-ink-accent-ui`}
            >
              {agent.name}
            </a>
          ) : (
            <span className={nameClass}>{agent.name}</span>
          )}
          {/* Product tokens, deliberately untranslated. */}
          <div className="mt-2 text-smycles-micro tracking-[0.08em] text-ink-muted">{spec}</div>
        </div>
        {/* Below @sm the action takes its own line under the name rather than squeezing it. */}
        {action ? <div className="basis-full @sm:ml-auto @sm:basis-auto @sm:shrink-0">{action}</div> : null}
      </div>

      {role.text ? (
        <div
          lang={role.lang}
          className="mt-6 whitespace-pre-line border-l-2 border-accent-decoration pl-4 text-smycles-body leading-relaxed text-ink"
        >
          {role.text}
        </div>
      ) : null}

      {covers.length > 0 ? (
        <div className="mt-7">
          <AgentColumn label={<FormattedMessage id="match.agent.covers" defaultMessage="Covers" />}>
            <div className="flex flex-wrap gap-2">
              {covers.map((c, i) => (
                <span key={i} lang={c.lang} className="box-flat bg-page px-2.5 py-1 text-smycles-label text-ink">
                  {c.text}
                </span>
              ))}
            </div>
          </AgentColumn>
        </div>
      ) : null}

      {/* Two columns, not three: the tool list is long while the integrations are a handful of
          chips, so a three-way split left most of the sheet empty. Stacking them over the
          guardrails fills the right column against the tools. */}
      {agent.tools.length + integrations.length + guardrails.length > 0 ? (
        <div className="mt-7 grid grid-cols-1 gap-x-12 gap-y-8 border-t border-ink/10 pt-6 @2xl:grid-cols-2">
          {agent.tools.length > 0 ? (
            <AgentColumn label={<FormattedMessage id="match.agent.tools" defaultMessage="Tools" />} count={agent.tools.length}>
              <ul className="space-y-3">
                {agent.tools.map((t, i) => (
                  <li key={i}>
                    <div className="font-mono text-smycles-meta text-ink">{t.name}</div>
                    {prose(t.description).text ? (
                      <div
                        lang={prose(t.description).lang}
                        className="whitespace-pre-line text-smycles-label leading-snug text-ink-muted"
                      >
                        {prose(t.description).text}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </AgentColumn>
          ) : null}

          <div className="space-y-8">
            {integrations.length > 0 ? (
              <AgentColumn
                label={<FormattedMessage id="match.agent.integrations" defaultMessage="Integrations" />}
                count={integrations.length}
              >
                <div className="flex flex-wrap gap-2">
                  {integrations.map((m, i) => (
                    <span key={i} className="box-flat px-2 py-1 text-smycles-label text-ink">
                      {m}
                    </span>
                  ))}
                </div>
              </AgentColumn>
            ) : null}

            {guardrails.length > 0 ? (
              <AgentColumn
                label={<FormattedMessage id="match.agent.guardrails" defaultMessage="Guardrails" />}
                count={guardrails.length}
              >
                <ol className="space-y-2">
                  {guardrails.map((g, i) => (
                    <li key={i} className="flex gap-3 text-smycles-label leading-snug text-ink">
                      <span className="font-mono text-ink-accent tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                      <span lang={g.lang} className="whitespace-pre-line">
                        {g.text}
                      </span>
                    </li>
                  ))}
                </ol>
              </AgentColumn>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
